export const ANALYTICS_EVENTS = Object.freeze({
  CTA_CLICK: "cta_click",
  FORM_VIEW: "form_view",
  FORM_START: "form_start",
  FORM_ERROR: "form_error",
  GENERATE_LEAD: "generate_lead",
  TRIAL_LEAD_SUBMITTED: "trial_lead_submitted",
  TRIAL_BOOKING_VIEWED: "trial_booking_viewed",
  TRIAL_BOOKING_COMPLETED: "trial_booking_completed",
  TRIAL_CALLBACK_REQUESTED: "trial_callback_requested",
})

export const CTA_LOCATIONS = Object.freeze({
  HEADER: "header",
  HERO: "hero",
  MID_PAGE: "mid_page",
  PROOF_SECTION: "proof_section",
  FORM: "form",
  STICKY_MOBILE: "sticky_mobile",
  FOOTER: "footer",
  BOTTOM: "bottom",
})

export const PROGRAM_INTERESTS = Object.freeze({
  BJJ: "bjj",
  MMA: "mma",
  KIDS_BJJ: "kids_bjj",
  KIDS_MARTIAL_ARTS: "kids_martial_arts",
  GENERAL: "general",
})

const CTA_LOCATION_VALUES = new Set(Object.values(CTA_LOCATIONS))
const PROGRAM_INTEREST_VALUES = new Set(Object.values(PROGRAM_INTERESTS))

const PROGRAM_BY_PAGE_PATH = Object.freeze({
  "/bjj-muenchen/": PROGRAM_INTERESTS.BJJ,
  "/mma-kickboxen-muenchen/": PROGRAM_INTERESTS.MMA,
  "/bjj-kinder-muenchen/": PROGRAM_INTERESTS.KIDS_BJJ,
  "/kampfsport-fuer-kinder-muenchen/": PROGRAM_INTERESTS.KIDS_MARTIAL_ARTS,
  "/kleine-ninjas-muenchen/": PROGRAM_INTERESTS.KIDS_MARTIAL_ARTS,
})

export function normalizePagePath(pathname = "/") {
  const pathWithoutQuery = String(pathname || "/").split(/[?#]/, 1)[0] || "/"
  const withLeadingSlash = pathWithoutQuery.startsWith("/")
    ? pathWithoutQuery
    : `/${pathWithoutQuery}`

  if (withLeadingSlash === "/") return "/"
  return withLeadingSlash.endsWith("/")
    ? withLeadingSlash
    : `${withLeadingSlash}/`
}

export function getProgramInterestForPath(pathname) {
  return (
    PROGRAM_BY_PAGE_PATH[normalizePagePath(pathname)] ||
    PROGRAM_INTERESTS.GENERAL
  )
}

export function normalizeProgramInterest(programInterest, pathname) {
  return PROGRAM_INTEREST_VALUES.has(programInterest)
    ? programInterest
    : getProgramInterestForPath(pathname)
}

export function normalizeCtaLocation(ctaLocation) {
  return CTA_LOCATION_VALUES.has(ctaLocation) ? ctaLocation : undefined
}

export function getDeviceType(viewportWidth) {
  const width = Number(viewportWidth)

  if (Number.isFinite(width) && width <= 767) return "mobile"
  if (Number.isFinite(width) && width <= 1023) return "tablet"
  return "desktop"
}

export function getVisibleCtaLabel(element) {
  const explicitLabel = element?.getAttribute?.("data-analytics-cta-label")
  const visibleLabel = element?.textContent
  return normalizeLabel(explicitLabel || visibleLabel || "") || undefined
}

export function buildAnalyticsContext({
  pagePath,
  ctaLocation,
  ctaLabel,
  programInterest,
  viewportWidth,
  errorType,
}) {
  const normalizedPagePath = normalizePagePath(pagePath)
  const normalizedCtaLocation = normalizeCtaLocation(ctaLocation)
  const normalizedProgramInterest = normalizeProgramInterest(
    programInterest,
    normalizedPagePath,
  )

  return compactParameters({
    page_path: normalizedPagePath,
    cta_location: normalizedCtaLocation,
    cta_label: normalizeLabel(ctaLabel || "") || undefined,
    program_interest: normalizedProgramInterest,
    device_type: getDeviceType(viewportWidth),
    error_type: errorType,
  })
}

export function getCtaEventParameters(element, location, viewportWidth) {
  const ctaLocation = element?.getAttribute?.("data-analytics-cta-location")
  const programInterest = element?.getAttribute?.(
    "data-analytics-program-interest",
  )

  if (!normalizeCtaLocation(ctaLocation)) return null

  return buildAnalyticsContext({
    pagePath: location?.pathname,
    ctaLocation,
    ctaLabel: getVisibleCtaLabel(element),
    programInterest,
    viewportWidth,
  })
}

export function trackAnalyticsEvent(eventName, parameters) {
  if (
    typeof window === "undefined" ||
    typeof window.__tlaTrackAnalyticsEvent !== "function"
  ) {
    return false
  }

  return window.__tlaTrackAnalyticsEvent(eventName, parameters) === true
}

function normalizeLabel(value) {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim()
}

function compactParameters(parameters) {
  return Object.fromEntries(
    Object.entries(parameters).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  )
}
